import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionInspectionDetail } from './hooks/useInspections';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  requested: 'bg-amber-100 text-amber-600',
  scheduled: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-indigo-100 text-indigo-600',
  submitted: 'bg-violet-100 text-violet-600',
  passed: 'bg-emerald-100 text-emerald-600',
  failed: 'bg-red-100 text-red-600',
  rework_required: 'bg-orange-100 text-orange-600',
  reinspection_required: 'bg-rose-100 text-rose-600',
  approved: 'bg-emerald-600 text-white',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  requested: 'Đã yêu cầu',
  scheduled: 'Đã lập lịch',
  in_progress: 'Đang kiểm tra',
  submitted: 'Đã nộp kết quả',
  passed: 'Đạt (Passed)',
  failed: 'Không đạt (Failed)',
  rework_required: 'Yêu cầu sửa lỗi',
  reinspection_required: 'Cần kiểm tra lại',
  approved: 'Đã phê duyệt',
  closed: 'Đã đóng',
  cancelled: 'Đã hủy',
};

const typeLabels: Record<string, string> = {
  incoming_material: 'Kiểm tra vật tư đầu vào',
  pre_installation: 'Kiểm tra trước thi công',
  in_process: 'Kiểm tra trong quá trình',
  hold_point: 'Điểm dừng kiểm tra (Hold Point)',
  final_acceptance: 'Nghiệm thu hoàn thành',
  handover: 'Bàn giao',
  safety: 'Kiểm tra an toàn',
  mockup: 'Kiểm tra mẫu (Mockup)',
  defect_check: 'Kiểm tra sai sót (Defect)',
  rework_verification: 'Xác minh sửa lỗi',
};

export default function InspectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { inspection: item, isLoading, updateInspection } = useConstructionInspectionDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết phiếu kiểm tra...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin phiếu kiểm tra</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateInspection({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái phiếu kiểm tra sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'checklist', label: 'Checklist kiểm tra' },
    { value: 'photos', label: 'Hình ảnh bằng chứng' },
    { value: 'references', label: 'Tham chiếu BOQ/WO' },
    { value: 'rework', label: 'Sửa lỗi (Rework)' },
    { value: 'documents', label: 'Tài liệu đính kèm' },
    { value: 'audit', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.inspection_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-shield-user-line" />
            {item.inspector?.full_name || 'Chưa có người kiểm tra'}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Phiếu kiểm tra', path: '/construction/inspections' },
        { label: item.inspection_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'requested' && (
            <AuthButton module="quality" action="update" variant="primary" icon="ri-calendar-event-line" onClick={() => handleStatusChange('scheduled')}>
              Lập lịch kiểm tra
            </AuthButton>
          )}
          {item.status_code === 'submitted' && (
            <AuthButton module="quality" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
              Phê duyệt kết quả
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-file-pdf-line">Xuất PDF</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Điểm đánh giá" value={item.score || 0} icon="ri-numbers-line" variant="primary" suffix=" / 100" />
                <KPICard title="Loại kiểm tra" value={typeLabels[item.inspection_type] || item.inspection_type} icon="ri-list-check-line" variant="info" />
                <KPICard title="Kết quả" value={item.result_code?.toUpperCase() || 'CHƯA CÓ'} icon="ri-checkbox-circle-line" variant={item.result_code === 'pass' ? 'success' : 'warning'} />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả yêu cầu kiểm tra
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.description || 'Chưa có thông tin mô tả chi tiết cho yêu cầu kiểm tra này.'}"
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-8 pt-6 border-t border-slate-50">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.zone?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Giai đoạn</span>
                    <span className="text-sm font-bold text-slate-700">Phase {item.phase_id || '01'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Cấp độ QA</span>
                    <span className="text-sm font-bold text-slate-700">{item.qa_level || 'L1'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-history-line text-slate-400" />
                  Lịch sử yêu cầu
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <Avatar name={item.requester?.full_name} size="xs" />
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold ">Người yêu cầu</p>
                        <p className="text-xs font-bold text-slate-700">{item.requester?.full_name}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400">{new Date(item.requested_at).toLocaleString('vi-VN')}</span>
                  </div>
                  {item.inspected_at && (
                    <div className="flex items-center justify-between p-3 bg-os-primary/5 rounded-2xl border border-os-primary/10">
                      <div className="flex items-center gap-3">
                        <Avatar name={item.inspector?.full_name} size="xs" />
                        <div>
                          <p className="text-[10px] text-os-primary font-bold ">Thời điểm kiểm tra</p>
                          <p className="text-xs font-bold text-slate-700">{item.inspector?.full_name}</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-400">{new Date(item.inspected_at).toLocaleString('vi-VN')}</span>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-shield-check-line" />
                  Kiểm soát sai sót
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-[10px] text-slate-400 font-bold ">Yêu cầu Rework</span>
                    <Badge variant={item.requires_rework ? 'danger' : 'neutral'} className="text-[9px]  font-bold">
                      {item.requires_rework ? 'REQUIRED' : 'NONE'}
                    </Badge>
                  </div>
                  {item.requires_rework && (
                    <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                      <p className="text-[10px] text-red-400 font-bold  mb-1">Hạn chót sửa lỗi</p>
                      <p className="text-sm font-bold text-red-200">{item.rework_deadline ? new Date(item.rework_deadline).toLocaleDateString('vi-VN') : 'Chưa thiết lập'}</p>
                    </div>
                  )}
                </div>
              </Card>

              {item.remarks && (
                <Alert variant="info" title="Nhận xét của thanh tra" className="text-xs">
                  {item.remarks}
                </Alert>
              )}

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-slate-400" />
                  Chứng từ liên quan
                </h4>
                <div className="space-y-2">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-file-list-line">Bản vẽ thiết kế thi công</Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-file-paper-line">Tiêu chuẩn kỹ thuật (Specs)</Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-camera-line">Ảnh chụp hiện trường WO</Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'checklist' || activeTab === 'photos' || activeTab === 'references' || activeTab === 'rework' || activeTab === 'documents' || activeTab === 'audit') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ checklist hiện trường kỹ thuật, kho ảnh bằng chứng nghiệm thu và các lệnh sửa lỗi thi công."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
